/**
 * BuyerChecklistModal
 *
 * Cohort 1 lead-magnet capture. Opens from the homepage hero (and could be
 * mounted anywhere else later). Calls /api/lead-capture/buyers-checklist
 * which:
 *   1. Persists the lead row in `lead_captures` (kind="buyers_checklist")
 *   2. Emails the visitor the generated PDF as a Resend attachment.
 *
 * Telemetry:
 *   - lead_magnet_modal_shown    { source }
 *   - lead_magnet_modal_submitted{ source, opted_in }
 *   - lead_magnet_modal_dismissed{ source, reason }
 */
import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import * as VisuallyHidden from "@radix-ui/react-visually-hidden";
import { X, Mail, Loader2, CheckCircle2, FileText, ListChecks, BookOpen } from "lucide-react";
import { toast } from "sonner";
import { captureBuyersChecklist } from "@/lib/api";
import { track } from "@/lib/analytics";

export default function BuyerChecklistModal({
    open,
    onOpenChange,
    source = "home_hero",
}) {
    const [email, setEmail] = useState("");
    const [firstName, setFirstName] = useState("");
    const [optIn, setOptIn] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [sent, setSent] = useState(false);

    useEffect(() => {
        if (open) {
            track("lead_magnet_modal_shown", { source });
        }
    }, [open, source]);

    const handleClose = (reason = "x_close") => {
        if (!sent) {
            track("lead_magnet_modal_dismissed", { source, reason });
        }
        onOpenChange?.(false);
        // Reset on next open
        setTimeout(() => {
            setSent(false);
            setSubmitting(false);
        }, 250);
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        if (submitting || !email.trim()) return;
        setSubmitting(true);
        try {
            await captureBuyersChecklist({
                email: email.trim().toLowerCase(),
                first_name: firstName.trim() || null,
                marketing_opt_in: optIn,
                source,
            });
            track("lead_magnet_modal_submitted", { source, opted_in: optIn });
            setSent(true);
            toast.success("Sent! Check your inbox in a moment.");
        } catch (err) {
            const msg =
                err?.response?.data?.detail ||
                err?.response?.data?.message ||
                "We couldn't send your checklist. Please try again.";
            toast.error(msg);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={(o) => (o ? onOpenChange?.(true) : handleClose("backdrop"))}>
            <DialogContent
                className="max-w-lg p-0 overflow-hidden bg-bl-surface border border-bl"
                data-testid="checklist-modal"
            >
                <VisuallyHidden.Root>
                    <DialogTitle>Free Home Inspection Checklist</DialogTitle>
                    <DialogDescription>
                        Email-gated download of the Buyer&apos;s Leverage home inspection checklist PDF.
                    </DialogDescription>
                </VisuallyHidden.Root>

                <button
                    type="button"
                    onClick={() => handleClose("x_close")}
                    className="absolute right-3 top-3 z-10 inline-flex items-center justify-center w-8 h-8 rounded-full text-bl-muted hover:text-bl hover:bg-bl-bg/50 transition-colors"
                    aria-label="Close"
                    data-testid="checklist-modal-close"
                >
                    <X className="w-4 h-4" />
                </button>

                {sent ? (
                    <div className="p-8" data-testid="checklist-modal-success">
                        <div className="flex items-start gap-3">
                            <CheckCircle2 className="h-6 w-6 text-[var(--bl-moss)] mt-0.5 shrink-0" />
                            <div>
                                <h3 className="font-display font-semibold text-xl text-bl">
                                    Your checklist is on the way.
                                </h3>
                                <p className="mt-2 text-sm text-bl-muted leading-relaxed">
                                    We just emailed it to{" "}
                                    <span className="font-mono text-bl">{email}</span> as a PDF
                                    attachment. If you don&apos;t see it within a few minutes,
                                    check spam — it&apos;s a fresh send from{" "}
                                    <span className="font-mono">buyersleverage.com</span>.
                                </p>
                                <button
                                    type="button"
                                    onClick={() => handleClose("stay")}
                                    className="bl-btn-secondary mt-6"
                                    data-testid="checklist-modal-done"
                                >
                                    Got it
                                </button>
                            </div>
                        </div>
                    </div>
                ) : (
                    <>
                        <div className="px-7 pt-7 pb-5 bg-[var(--bl-tint)]/40 border-b border-bl">
                            <div className="bl-eyebrow">
                                <FileText className="w-3.5 h-3.5" /> Free download
                            </div>
                            <h3 className="mt-3 font-display font-semibold text-2xl text-bl leading-tight">
                                The Home Inspection Checklist.
                            </h3>
                            <p className="mt-2 text-sm text-bl-muted leading-relaxed">
                                60+ items across roof, foundation, mechanicals, interior,
                                attic, and paperwork — plus a 5-rule negotiation primer.
                                Print it. Walk the property with confidence.
                            </p>
                            <ul className="mt-4 space-y-1.5 text-xs text-bl-muted">
                                {[
                                    [ListChecks, "Section-by-section walkthrough"],
                                    [BookOpen, "Negotiation primer included"],
                                    [Mail, "Emailed as a PDF — no signup, no spam"],
                                ].map(([Ico, t]) => (
                                    <li key={t} className="inline-flex items-center gap-2 mr-4">
                                        <Ico className="w-3.5 h-3.5 text-bl-primary" />
                                        {t}
                                    </li>
                                ))}
                            </ul>
                        </div>

                        <form onSubmit={onSubmit} className="p-7" data-testid="checklist-modal-form">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
                                <input
                                    type="text"
                                    autoComplete="given-name"
                                    placeholder="First name (optional)"
                                    value={firstName}
                                    onChange={(e) => setFirstName(e.target.value)}
                                    className="bl-input"
                                    data-testid="checklist-modal-firstname"
                                />
                                <input
                                    type="email"
                                    autoComplete="email"
                                    required
                                    placeholder="you@example.com"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    className="bl-input"
                                    data-testid="checklist-modal-email"
                                />
                            </div>

                            <label className="flex items-start gap-2 text-xs text-bl-muted cursor-pointer mb-5">
                                <input
                                    type="checkbox"
                                    checked={optIn}
                                    onChange={(e) => setOptIn(e.target.checked)}
                                    className="mt-0.5 accent-[var(--bl-primary)]"
                                    data-testid="checklist-modal-optin"
                                />
                                <span>
                                    Send me strategic buyer insights and homeowner tips
                                    occasionally. (One-click unsubscribe in every email.)
                                </span>
                            </label>

                            <button
                                type="submit"
                                disabled={submitting}
                                className="bl-btn-primary justify-center gap-2 w-full"
                                data-testid="checklist-modal-submit"
                            >
                                {submitting ? (
                                    <>
                                        <Loader2 className="h-4 w-4 animate-spin" />
                                        Sending…
                                    </>
                                ) : (
                                    "Email me the checklist"
                                )}
                            </button>

                            <p className="mt-3 text-xs text-bl-muted text-center">
                                No credit card. No signup. We&apos;ll never share your email.
                            </p>
                        </form>
                    </>
                )}
            </DialogContent>
        </Dialog>
    );
}
